import { useEffect, useRef } from "react";
import Tealium, { EventLog } from "@4tn/webx-analytics";
import { AudioType, CONTENT_LANGUAGE } from "@constants/consts";
import { TEALIUM_PAGE_CATEGORY } from "@constants/tealium.constants";
import isCampaignActive from "@utils/pageContent/isCampaignActive";
import { Episode } from "@utils/video/dataModels";
import getPageCategory, { getRouteSlug } from "./getPageCategory";

interface PageViewProps {
  isLoading: boolean;
  data: unknown;
  error?: unknown;
}

const getContentMetadata = (entry: unknown): Record<string, string | null> => {
  if (!entry) return {};

  const { title, createdAt, updatedAt } = entry as Article | Page | CategoryEntry | Video | Episode;
  const fields: Record<string, string | null> = {
    content_name: title || null,
    content_language: CONTENT_LANGUAGE,
    content_datecreated: createdAt || null,
    content_datepublished: createdAt || null,
    content_datemodified: updatedAt || null,
  };

  if ((entry as Article).category) {
    fields.content_category = (entry as Article).category.title;
  }

  if ((entry as Episode).subtitle) {
    const episode = entry as Episode;
    fields.content_name = `${episode.title}: ${episode.subtitle}`;
  }

  return fields;
};

const getContentType = (pageCategory: string | null, data: unknown): string | null => {
  if (pageCategory === TEALIUM_PAGE_CATEGORY.STATION_PAGE) {
    return "theme station";
  }

  if (pageCategory === TEALIUM_PAGE_CATEGORY.SHOW_PAGE) {
    return AudioType.SHOW;
  }

  if (pageCategory === TEALIUM_PAGE_CATEGORY.CAMPAIGN) {
    const { startDate, endDate } = data as Campaign;
    const campaignStatus = isCampaignActive(startDate, endDate);

    return `campaign_${campaignStatus}`;
  }

  if (pageCategory === TEALIUM_PAGE_CATEGORY.POPOUT) {
    return "popout player";
  }

  return pageCategory;
};

const usePageView = ({ isLoading, data, error }: PageViewProps) => {
  const hasSentAdvertorialEvent = useRef(false);

  useEffect(() => {
    if (isLoading || (data === undefined && !error)) return;
    if (EventLog.getLastEvent("view")?.page_url !== document.location.href) {
      const pageCategory = getPageCategory(
        document.location.pathname,
        (data as Article)?.hasVideo ?? false,
        !!((data as Article)?.sponsor ?? false)
      );
      const pageId = getRouteSlug(document.location.pathname);

      Tealium.setDataLayer({
        page_category: pageCategory,
        page_id: pageId,
      });

      const contentMetadata = getContentMetadata(data);
      const contentType = getContentType(pageCategory, data);

      Tealium.view({
        event_label: document.title,
        page_id: pageId,
        page_category: pageCategory,
        content_id: pageId,
        content_type: contentType,
        ...contentMetadata,
      });

      if ((data as Article)?.sponsor && !hasSentAdvertorialEvent.current) {
        const timer = setTimeout(() => {
          Tealium.link({
            event_action: "advertorials",
            event_category: "actual_time_on_page",
            event_label: 15,
            content_id: pageId,
            content_type: pageCategory,
            ...contentMetadata,
          });
          hasSentAdvertorialEvent.current = true;
        }, 15000);

        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, data, error]);
};

export default usePageView;
